import React, { useState, useEffect } from 'react';
import clsx from 'clsx';

import { withStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';

import TextInput from 'libraries/TextInput';
import { updateBreezy, getBreezySettings, authorizeBreezy, updateBreezyCredentials, resetBreezy } from 'requests/SubscriptionRequests';
import Switch from 'libraries/SwitchBrandWithLabel';
import CopyToClipboard from 'react-copy-to-clipboard';
import useDebounce from 'hooks/useDebounce';
import NumberInput from 'components/inputs/NumberInput';
import SettingsEvents from 'events/SettingsEvents';
import EditIcon from './edit.svg';
import stylesJS from './styles';
import EditDialog from './EditDialog';

const BreezyContent = ({ flashMessage, classes, company, updateCompany }) => {
    const [credentials, setCredentials] = useState({ secretKey: '', email: '' });
    const [enabled, setEnabled] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);
    const [reapplyTimeFrame, setReapplyTimeFrame] = useState(180);
    const [reapplyTimeFrameTouched, setReapplyTimeFrameTouched] = useState(false);
    const [stageName, setStageName] = useState('');
    const [stageNameTouched, setStageNameTouched] = useState(false);
    const [authorized, setAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    useEffect(() => {
        getBreezySettings()
            .then(({ success, data }) => {
                if (success && data) {
                    setEnabled(data.enabled);
                    setHasPassword(data.hasPassword);
                    setReapplyTimeFrame(data.reapplyTimeFrame);
                    setStageName(data.stageName);
                    setAuthorized(data.authorized);
                    setWebhookUrl(data.webhookUrl);
                    setCredentials({
                        secretKey: data.secretKey,
                        email: data.email
                    });
                }
            })
            .finally(() => setLoading(false));
    }, []);

    useDebounce(credentials, 300, () => {
        if (!authorized && enabled) {
            updateBreezyCredentials(credentials).then(() => refreshSettings());
        }
    });
    useDebounce(stageName, 300, () => {
        if (stageNameTouched) updateSettings('Stage Name updated');
    });
    const isValidTimeframe = reapplyTimeFrameTouched && +reapplyTimeFrame > 0;
    useDebounce(reapplyTimeFrame, 300, () => {
        if (isValidTimeframe) updateSettings();
    });

    const refreshSettings = () => {
        getBreezySettings()
            .then(({ success, data }) => {
                if (success && data) {
                    setEnabled(data.enabled);
                    setHasPassword(data.hasPassword);
                    setReapplyTimeFrame(data.reapplyTimeFrame);
                    setStageName(data.stageName);
                    setAuthorized(data.authorized);
                    setWebhookUrl(data.webhookUrl);
                }
            });
    };

    const updateSettings = (message) => {
        const newSettings = {
            stageName
        };
        if (isValidTimeframe) {
            newSettings.reapplyTimeFrame = reapplyTimeFrame;
        }
        updateBreezy(newSettings)
            .then(({ success, data }) => {
                if (success) {
                    setEnabled(data.enabled);
                    if (message) flashMessage(message, 'done');
                } else {
                    flashMessage('Something went wrong', '');
                }
            });
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    };

    const handleSwitchChange = (e) => {
        const { checked } = e.target;
        updateBreezy({ enabled: checked })
            .then(({ success, data }) => {
                if (success) {
                    SettingsEvents.BREEZY_ENABLED({ enabled: data.enabled });
                    setEnabled(data.enabled);
                    updateCompany({ ...company, isShowBreezyUnauthorizedBanner: (data.enabled && !data.authorized) });
                    refreshSettings();
                    flashMessage(`Integration has been turned ${data.enabled ? 'on' : 'off'}`, 'done');
                } else {
                    flashMessage('Something went wrong', '');
                }
            });
    };

    const handleAuthorize = () => {
        setAuthLoading(true);
        authorizeBreezy(credentials).then(({ success }) => {
            if (success) {
                setAuthorized(true);
                updateCompany({ ...company, isShowBreezyUnauthorizedBanner: false });
                flashMessage('Authorized', 'done');
            }
        }).catch(() => {
            flashMessage('Credentials are invalid', 'error');
        }).finally(() => setAuthLoading(false));
    };

    const handleReset = () => {
        resetBreezy().then(({ success }) => {
            if (success) {
                SettingsEvents.BREEZY_RESETED();
                setCredentials({
                    secretKey: '',
                    email: ''
                });
                refreshSettings();
                updateCompany({ ...company, isShowBreezyUnauthorizedBanner: true });
            }
        });
    };

    const updateCredentials = (newData) => {
        setLoading(true);
        updateBreezyCredentials(newData).then((res) => {
            if (res.success) {
                setEditDialogOpen(false);
                setHasPassword(res.data.hasPassword);
                setAuthorized(res.data.authorized);
                setCredentials({
                    secretKey: res.data.secretKey,
                    email: res.data.email
                });
                updateCompany({ ...company, isShowBreezyUnauthorizedBanner: !res.data.authorized });
            }
        }).finally(() => setLoading(false));
    };

    const handleDays = (e) => {
        let inputValue = e.target.value;
        if (inputValue > 9999) {
            inputValue = 9999;
        }
        setReapplyTimeFrameTouched(true);
        setReapplyTimeFrame(inputValue);
    };

    if (loading) {
        return (
            <div className={classes.progressWrapper}>
                <CircularProgress size={30} className={classes.progress} />
            </div>
        );
    }

    return (
        <div>
            <h3 className={classes.heading}>Connect Vervoe to Breezy</h3>

            <Switch
                label="Enable Breezy Integration"
                checked={enabled}
                onChange={handleSwitchChange}
            />

            {(enabled || authorized) && (
                <div>
                    <div className={classes.textFieldWrapper}>
                        <CopyToClipboard text={webhookUrl} onCopy={() => flashMessage('Webhook URL copied to clipboard')}>
                            <Button color="primary" className={classes.buttonRoot}>COPY LINK</Button>
                        </CopyToClipboard>
                        <TextInput
                            label="Webhook url"
                            fullWidth
                            disabled
                            value={webhookUrl}
                            className={classes.input}
                            variant="outlined"
                        />
                    </div>

                    <div className="u-dsp--f u-ai--center">
                        <TextInput
                            label="Shared Secret key"
                            name="secretKey"
                            value={credentials.secretKey}
                            onChange={handleChange}
                            disabled={authorized}
                            className={clsx('u-mrg--bx2', classes.input)}
                            fullWidth
                            variant="outlined"
                        />
                        <Button
                            color="primary"
                            variant="text"
                            onClick={() => setEditDialogOpen(true)}
                            disabled={!authorized || !enabled}
                        >
                            <img src={EditIcon} alt="" />
                        </Button>
                    </div>
                    <p className={classes.textFieldLabel}>Please paste Shared Secret Key in order to allow the integration.</p>

                    <div className="u-dsp--f u-ai--center">
                        <TextInput
                            label="Email"
                            name="email"
                            value={credentials.email}
                            onChange={handleChange}
                            disabled={authorized}
                            className={clsx('u-mrg--bx2', classes.input)}
                            fullWidth
                            variant="outlined"
                        />
                        <Button
                            color="primary"
                            variant="text"
                            onClick={() => setEditDialogOpen(true)}
                            disabled={!authorized || !enabled}
                        >
                            <img src={EditIcon} alt="" />
                        </Button>
                    </div>
                    <p className={classes.textFieldLabel}>Please enter the email address of the Breezy user.</p>
                    <div className="u-dsp--f u-ai--center">
                        <TextInput
                            label="Password"
                            name="password"
                            type="password"
                            value={authorized ? '--------------------' : credentials.password}
                            onChange={handleChange}
                            disabled={authorized}
                            className={clsx('u-mrg--bx2', classes.input)}
                            fullWidth
                            variant="outlined"
                        />
                        <Button
                            color="primary"
                            variant="text"
                            onClick={() => setEditDialogOpen(true)}
                            disabled={!authorized || !enabled}
                        >
                            <img src={EditIcon} alt="" />
                        </Button>
                    </div>
                    <p className={classes.textFieldLabel}>Please enter the password of the Breezy user.</p>

                    <div className="u-dsp--f u-mrg--tx8">
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.saveButton}
                            onClick={handleAuthorize}
                            disabled={!credentials.password
                                || !credentials.email
                                || !credentials.secretKey
                                || !hasPassword
                                || authorized
                                || authLoading
                            }
                        >
                            Authorize
                        </Button>
                        <Button
                            color="primary"
                            variant="text"
                            className={classes.resetButton}
                            onClick={handleReset}
                            disabled={!authorized || !enabled}
                        >
                            Reset
                        </Button>
                    </div>

                    {authorized && (
                        <div className="u-mrg--tx6">
                            <TextInput
                                label="Vervoe Stage Name"
                                value={stageName}
                                onChange={(e) => {
                                    setStageNameTouched(true);
                                    setStageName(e.target.value);
                                }}
                                disabled={!enabled}
                                className={clsx('u-mrg--bx2', classes.input)}
                                fullWidth
                                variant="outlined"
                            />
                            <p className={classes.textFieldLabel}>The Vervoe stage name must match the stage name used in Breezy.</p>

                            <div className={classes.subtitle}>Set Reinvite Timeframe</div>
                            <div className="u-dsp--f u-ai--center u-jc--start u-mrg--tx3">
                                <span className="u-mrg--rx2 u-txt--semibold">Allow candidates to take this assessment again</span>
                                <NumberInput
                                    value={reapplyTimeFrame}
                                    onChange={handleDays}
                                    type="number"
                                    min="1"
                                    max={9999}
                                    disabled={!enabled}
                                    classes={{
                                        root: classes.numberInput
                                    }}
                                />
                                <span className="u-mrg--lx2 u-txt--semibold">days after their last attempt</span>
                            </div>
                            <p className="u-txt--normal u-mrg--tx3">
                                If the set time has passed or the candidate’s status is Expired or More time requested,
                                they’ll be re-invited to restart the assessment from scratch.
                            </p>
                        </div>
                    )}
                </div>
            )}
            <EditDialog
                open={editDialogOpen}
                updateCredentials={updateCredentials}
                handleClose={() => setEditDialogOpen(false)}
                otherClasses={classes}
                loading={loading}
                initialValue={{
                    email: credentials.email,
                    secretKey: credentials.secretKey
                }}
            />
        </div>
    );
};

export default withStyles(stylesJS)(BreezyContent);
